import Moment from 'moment';

import regexMap from '../constants/regex-map';
import cardRegex from '../constants/card-regex';
import passengerTypes from '../constants/passenger-types-enum';

import { PhoneNumberUtil } from 'google-libphonenumber/dist/libphonenumber';

import { lookupTypes } from '../constants/pnr-lookup-types-enum';
import FFP from '../constants/frequent-flyer-program-enum';
import {
  frequentFlyerNumberRegex,
  corporateFrequentFlyerRegex,
} from '../constants/frequent-flyer-number-regex';
import { consecutiveDigits, verifyClubPremierNumber, verifyWithLuhnAlgorithm } from '../utilities/form-helpers';
import { deleteSpacesText } from '../utilities/helpers';
import store from '../../redux/store/index';

const isAlphanumeric = value => regexMap.ALPHANUMERIC_ONLY.test(value);
const isAlphanumericDashes = value => regexMap.ALPHANUMERIC_AND_DASHES.test(value);
const isAlphanumericSpace = value => regexMap.ALPHANUMERIC_SPACE.test(value);
const isAlphanumericBoth = value => regexMap.ALPHANUMERIC_BOTH.test(value); // At least ONE DIGIT AND ONE LETTER
const isName = value => regexMap.IS_NAME_SABRE.test(value);
const isNumeric = value => regexMap.NUMERIC_ONLY.test(value);
const isDate = value => regexMap.IS_DATE_FORMAT.test(value); // required format 'MM-DD-YYYY'
const isEmpty = value => value === undefined || value === null || value === '';
const isInteger = value => Number.isInteger(Number(value));
const isValidNumberVirginAtlantic = value => regexMap.IS_VIRGIN_ATLANTIC_NUMBER.test(value);
const join = (rules) => (value, data) => rules.map(rule => rule(value, data)).filter(error => !!error)[0];
const noSpecialChars = value => !regexMap.ALPHA_AND_DASHES_ONLY.test(value);

const isMasterCard = value => cardRegex.MASTER.test(value);
const isVisa = value => cardRegex.VISA.test(value);
const isAmex = value => cardRegex.AMEX.test(value);
const isCarnet = value => cardRegex.CARNET.test(value);
const isJCB = value => cardRegex.JCB.test(value);
const isUATP = value => cardRegex.UATP.test(value);
const isEMDVoucher = value => cardRegex.EMD_VOUCHER.test(value);
const isPasswordCP = value => regexMap.PASSWORD_CLUBPREMIER.test(value);
const isPasswordCE = value => regexMap.PASSWORD_CLUBPREMIER_NOTSPECIALCHARACTER.test(value);
const TRAVELER_MAX_AGE = 98;

const phoneUtil = PhoneNumberUtil.getInstance();
// _modNValidator creates a validator for verification digits using MOD(n)(val)
const _modNValidator = (n) => (value) => {
  const valueInt = parseInt(value.substr(0, value.length - 1), 10);
  const originalIntCheckDigit = parseInt(value.substr(value.length - 1, 1), 10);
  const checkDigit = valueInt % n;
  return checkDigit === originalIntCheckDigit;
};
const validateFFPMod7 = _modNValidator(7);

export const required = (value) => {
  if (!value) {
    return 'This field is required!';
  }
};

export const number = value => value && isNaN(Number(value)) ? 'Must be a number' : undefined;

/**
 * @param {String} value - billing address street entered
 * @return {String} copyID of error message, if validation fails
 */
export function addressStreet(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_STREET'; // Please provide the billing street address
  } else if (!isAlphanumericSpace(value)) {
    return 'REVIEW_VALIDATION_STREET_NO_SPECIAL_CHARS'; // Please provide a street address with no special characters
  }
}

/**
 * @param {String} value - billing address apartment/unit entered
 * @return {String} copyID of error message, if validation fails
 */
export function addresApartment(value) {
  if (!isEmpty(value)) {
    if (!isAlphanumericSpace(value)) {
      return 'REVIEW_VALIDATION_APT_NO_SPECIAL_CHARS'; // Please provide a number with no special characters
    }
  }
}

export function addressApartmentBooking(value) {
  if (!isEmpty(value) && !isAlphanumericSpace(value)) {
    return 'REVIEW_VALIDATION_APT_NO_SPECIAL_CHARS'; // Please provide a Apt/Unit no. with no special characters
  }
}

/**
 * @param {String} value - billing address city entered
 * @return {String} copyID of error message, if validation fails
 */
export function addressCity(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_CITY'; // Please provide the billing city
  } else if (noSpecialChars(value)) {
    return 'REVIEW_VALIDATION_CITY_NO_SPECIAL_CHARS'; // Please provide a city with no special characters
  }
}

/**
 * @param {String} value - billing address state entered
 * @return {String} copyID of error message, if validation fails
 */
export function addressState(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_STATE'; // Please provide the billing state/province
  } else if (!isAlphanumericSpace(value)) {
    return 'REVIEW_VALIDATION_STATE_NO_SPECIAL_CHARS'; // Please provide state/province with no spceial characters
  }
}

/**
 * @param {String} value - billing address zip entered
 * @return {String} copyID of error message, if validation fails
 */
export function addressZip(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_ZIP'; // Please provide the billing zipcode
  } else if (!isAlphanumericDashes(value)) {
    return 'REVIEW_VALIDATION_ZIP_NO_SPECIAL_CHARS'; // Please provide a zipcode with no special characters
  }
}

export function addressStateUSCanada(value, data) {
  const hasNoState = data.addressCountry !== 'US' && data.addressCountry !== 'CA';
  if (hasNoState) return;

  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_STATE'; // Please provide the billing state/province
  } else if (!isAlphanumericSpace(value)) {
    return 'REVIEW_VALIDATION_STATE_NO_SPECIAL_CHARS'; // Please provide state/province with no spceial characters
  }
}

/**
 * @param {String} value - billing phone number data
 * @return {String} copyID of error message, if validation fails
 */
export function billingPhone(value) {
  if (isEmpty(value) || (value && isEmpty(value.phoneNumber))) {
    return 'REVIEW_VALIDATION_PHONE'; // Please provide the billing phone number
  } else if (value && !isEmpty(value.phoneNumber)) {
    return validatePhoneNumber(value);
  }
}

/**
 * @param {String} value - email entered
 * @param {Object} data - additional data
 * @param {Boolean} data.isPrimaryTraveler - is form for the primary passenger
 * @return {String} copyID of error message, if validation fails
 */
export function billingEmail(value) {
  if (isEmpty(value)) {
    return 'APP_VALIDATION_EMAIL_EMPTY';
  } else if (!isEmpty(value) && !regexMap.IS_EMAIL.test(value)) {
    return 'APP_VALIDATION_EMAIL_INVALID';
  }
}

/**
 * @param {String} value - cardholder name entered
 * @return {String} copyID of error message, if validation fails
 */
export function cardholderName(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_NAME'; // Please provide the credit card holders' name
  } else if (noSpecialChars(value)) {
    return 'REVIEW_VALIDATION_NAME_NO_SPECIAL_CHARS'; // Please provide a name with no special characters.
  }
}

export function clubPrem(value) {
  const duplicateTutorEnrollment = store.getState().Login.duplicateTutorEnrollment;

  const trimmedVal = value && value.replace(/\[0-9]/g, '');

  if (isEmpty(trimmedVal)) {
    return 'APP_VALIDATION_CLUB_PREMIER_EMPTY'; // Please provide a valid club premier number
  } else if (!isInteger(trimmedVal)) {
    return 'APP_VALIDATION_CLUB_PREMIER_FAILURE'; // The data has to be numbers and 9 digits
  } else if (!verifyClubPremierNumber(trimmedVal)) {
    return 'APP_VALIDATION_CLUB_PREMIER_FAILURE';
  } else if (duplicateTutorEnrollment) {
    return duplicateTutorEnrollment.copyID;
  }
}

/**
 * @param {String} value - card number entered
 * @return {String} copyID of error message, if validation fails
 */
export function cardNumber(value) {
  const validCreditCardsNumbers = ( isMasterCard(value) || isVisa(value) || isAmex(value) || isCarnet(value) || isJCB(value) );
  const isValidCreditCard = validCreditCardsNumbers && verifyWithLuhnAlgorithm(value);

  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_CARD'; // Please provide a credit card number
  } else if (regexMap.NON_NUMERALS.test(value)) {
    return 'REVIEW_VALIDATION_CARD_VALID'; // Please provide a valid credit card number
  } else {
    return !isValidCreditCard ? 'REVIEW_VALIDATION_CARD_VALID' : null; // Please provide a valid credit card number
  }
}

/**
 * @param {String} value - cvc entered
 * @param {Object} data - additional data
 * @param {Boolean} data.cardType - type of credit card entered
 * @return {String} error message, if validation fails
 */
export function cvc(value, data) {
  // cvc value is stripped of whitespace on blur
  const trimmedVal = value && value.replace(/\s/g, '');
  
  if (isEmpty(trimmedVal)) {
    return 'REVIEW_VALIDATION_CVC'; // Please provide a CVC number
  } else if (!isInteger(trimmedVal) || (trimmedVal.length !== 4 && trimmedVal.length !== 3)
    || (isAmex(data.cardNumber) && trimmedVal.length !== 4)
    || ((isMasterCard(data.cardNumber) || isVisa(data.cardNumber) || isCarnet(data.cardNumber)  || isJCB(data.cardNumber)) && trimmedVal.length !== 3)) {
    return 'REVIEW_VALIDATION_CVC_VALID'; // Please provide a valid CVC
  }
}

/**
 * @param {String} value - corporate frequent flyer number entered
 * @return {String} copyID of error message, if validation fails
 */
export function corporateFrequentFlyerNumber(value, data) {
  const { corporateFrequentFlyerProgram, corporateFrequentFlyerNumber } = data;
  const isClubPremier = corporateFrequentFlyerProgram === FFP.AEROMEXICO;
  const isDelta = corporateFrequentFlyerProgram === FFP.DELTA;
  const corporateCPRequired = isClubPremier && !corporateFrequentFlyerNumber;
  const corporateDeltaRequired = isDelta && !corporateFrequentFlyerNumber;
  const isNotNine = value && value.length !== 9;
  if (isClubPremier) {
    if (isEmpty(value) && corporateCPRequired) {
      return 'PAX_VALIDATION_FFP_EMPTY_CLUB_PREMIER';
    } else if (isNotNine) {
      return 'PAX_VALIDATION_FFP_CHARACTERS_CP';
    }
  }

  if (isDelta) {
    if (isEmpty(value) && corporateDeltaRequired) {
      return 'PAX_VALIDATION_FFP_EMPTY_DELTA';
    } else if (value && !isCorrectRule(corporateFrequentFlyerRegex[FFP.DELTA], value)) {
      return 'PAX_VALIDATION_FFP_CHARACTERS_DELTA';
    }
  }
}

/**
 * @param {String} value - date of birth entered
 * @param {Object} data - additional data
 * @param {Boolean} data.paxType - type of passenger, e.g. ADULT, CHILD, INFANT
 * @param {Date} data.departureDate - date of departing flight
 * @return {String/Object} error message, if validation fails
 */
export function dateOfBirth(value, data) {
  const y = value && parseInt(value.slice(0, 4), 10);
  const m = value && parseInt(value.slice(5, 7), 10);
  const d = value && parseInt(value.slice(8, 10), 10);
  if (isEmpty(value) && !data.requiredDateOfBirth) {
    return null;
  } else if (isEmpty(value)
    || !isDate
    || m === 0
    || d === 0
    || value.replace(regexMap.NON_NUMERALS, '').length !== 8) {
    return 'PAX_VALIDATION_DOB';
  } else {
    const departingDate = Moment.utc(data.departureDate);
    const enteredDate = Moment.utc(value, 'YYYY-MM-DD');
    const yearDiff = departingDate.diff(enteredDate, 'years');
    const lastDayOfSelectedMonth = Moment(`${ y }-${ m }`, 'YYYY-MM').endOf('month').date();

    // check month number
    if (m < 1 || m > 12) {
      // Verify month is in range
      return 'PAX_VALIDATION_MONTH';
    } else if (d > lastDayOfSelectedMonth) {
      // Verify day selected is in month (e.g. only 28/29 days in Feb)
      return {
        copyID: 'PAX_VALIDATION_DATE_MONTH',
        data: Moment(m, 'MM').format('MMMM'),
      };
    } else if (yearDiff > TRAVELER_MAX_AGE) {
      // Verify traveler is less than 99 years of age
      return 'PAX_VALIDATION_YEAR_MIN';
    } else if (data.paxType === passengerTypes.ADULT && yearDiff < 12) {
      // Verify adult age
      return 'PAX_VALIDATION_ADULTS';
    } else if (data.paxType === passengerTypes.CHILD && (yearDiff >= 12 || yearDiff < 2)) {
      // Verify child age
      return 'PAX_VALIDATION_CHILDREN';
    } else if (data.paxType === passengerTypes.INFANT && yearDiff >= 2) {
      // Verify infant max age
      return 'PAX_VALIDATION_INFANT_MAX';
    } else if (data.paxType === passengerTypes.INFANT
      && Moment(departingDate).subtract(7, 'd').isBefore(enteredDate)) {
      // Verify infant min age
      return 'PAX_VALIDATION_INFANT_MIN';
    }
  }
}

/**
 * @param {String} value - email entered
 * @param {Object} data - additional data
 * @param {Boolean} data.isPrimaryTraveler - is form for the primary passenger
 * @return {String} copyID of error message, if validation fails
 */
export function email(value, data) {
  if (data.isPrimaryTraveler && isEmpty(value)) {
    return 'APP_VALIDATION_EMAIL_EMPTY';
  } else if (!isEmpty(value) && !regexMap.IS_EMAIL.test(value)) {
    return 'APP_VALIDATION_EMAIL_INVALID';
  }
}

/**
 * @param {String} value - expiry date selected
 * @return {String} copyID of error message, if validation fails
 */
export function expirationDate(value, data) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_EXPIRY_VALID';
  } else {
    const date = `${ data.year }-${ data.month }`;
    const today = Moment().format('YYYY-MM');
    const enteredDate = Moment(date, 'YYYY-MM');

    if (Moment(today, 'YYYY-MM').isAfter(enteredDate)) {
      // check not in future
      return 'REVIEW_VALIDATION_EXPIRY_VALID'; // Please provide a valid expiration date
      // return 'Please provide a valid expiration date';
    }
  }
}

export function expirationGiftCardDate(value, data) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_EXPIRY_VALID';
  } else {
    const date = `${ data.year }-${ data.month }`;
    const today = Moment().format('YYYY-MM');
    const enteredDate = Moment(date, 'YYYY-MM');

    if (Moment(today, 'YYYY-MM').isAfter(enteredDate)) {
      // check not in future
      return 'REVIEW_VALIDATION_EXPIRY_VALID'; // Please provide a valid expiration date
      // return 'Please provide a valid expiration date';
    }
  }
}

/**
 * @param {String} value - frequent flyer number entered
 * @return {String} copyID of error message, if validation fails
 */
export function frequentFlyerNumber(value, data) {
  if (!isEmpty(value) || data.frequentFlyerPrograms !== null) {
    const { frequentFlyerProgram, corporateFrequentFlyerNumber } = data;

    const isClubPremier = frequentFlyerProgram === FFP.AEROMEXICO;
    const isAeromar = frequentFlyerProgram === FFP.AEROMAR;
    const hasCorporateFFN = corporateFrequentFlyerNumber ? true : false;
    const validFormatVirginAtlantic = isValidNumberVirginAtlantic(value);
    const isFFPSelected = !!frequentFlyerProgram;
    const isFFNRequired = isClubPremier ? !hasCorporateFFN : isFFPSelected;

    switch (frequentFlyerProgram) {
      case FFP.KOREAN: // Para Korean Air Sky Pass

        if (isEmpty(value)) {
          if (isFFNRequired) {
            return 'PAX_VALIDATION_FFP_EMPTY';
          }
        } else if (!isEmpty(value)) {
          const isREgExpoKL = (frequentFlyerNumberRegex[frequentFlyerProgram] && !isKoreaNumber(value, frequentFlyerProgram));
          if (!isAlphanumeric(value) || isREgExpoKL) {
            return 'PAX_VALIDATION_FFP_INVALID';
          }
        }
        break;
      case FFP.AEROFLOT:
        return _AeroflotValidation(value);
      case FFP.JET_AIRWAYS:
        if (isEmpty(value) && isFFNRequired) {
          return 'PAX_VALIDATION_FFP_EMPTY';
        }

        if (!validateFFPMod7(value)) {
          return 'PAX_VALIDATION_FFP_INVALID';
        }
        break;
      case FFP.VIRGIN_ATLANTIC:
        if (isEmpty(value)) {
          return 'PAX_VALIDATION_FFP_INVALID';
        }

        if (!validFormatVirginAtlantic) {
          return 'PAX_VALIDATION_FFP_INVALID';
        }
        break;
      case FFP.SAS:
        if (isEmpty(value)) {
          return 'PAX_VALIDATION_FFP_INVALID';
        }
        const isSASFFN = frequentFlyerNumberRegex[frequentFlyerProgram].test(value);
        if (!isSASFFN) {
          return 'PAX_VALIDATION_FFP_INVALID';
        }
        break;
      default: // Para ClubPremier y AEROMAR tenia este PAX_VALIDATION_FFP_EMPTY_CLUB_PREMIER pero pidieron cambiarlo
        if (isEmpty(value)) {
          if (isFFNRequired) {
            return 'PAX_VALIDATION_FFP_EMPTY';
          }
        } else if (!isEmpty(value)) {
          if (!isAlphanumeric(value) ||
            (frequentFlyerNumberRegex[frequentFlyerProgram] &&
              !frequentFlyerNumberRegex[frequentFlyerProgram].test(value)) || regexMap.ZERO_FFN_CHECK.test(value)) {
            return 'PAX_VALIDATION_FFP_INVALID';
          } else if (!isAlphanumeric(value) || ((isClubPremier || isAeromar) && !verifyClubPremierNumber(value.toString().replace(/\s+/g, '')))) {
            return isClubPremier ? 'CLUB_PREMIER_VALIDATION_FAILURE' : 'PAX_VALIDATION_FFP_INVALID';
          }
        }
    }
  }
}

/**
 * @param {String} value - email entered
 * @return {String} copyID of error message, if validation fails
 */
export function genericEmail(value) {
  if (isEmpty(value)) {
    return 'APP_VALIDATION_EMAIL_EMPTY';
  } else if (!regexMap.IS_EMAIL.test(value)) {
    return 'APP_VALIDATION_EMAIL_INVALID';
  }
}
/**
 * @param {String} value - email entered
 * @return {String} copyID of error message, if validation fails
 */
export function enrollmentEmail(value) {
  const duplicateEmailEnrollment = store.getState().Login.duplicateEmailEnrollment;

  if (isEmpty(value)) {
    return 'APP_VALIDATION_EMAIL_EMPTY';
  } else if (!regexMap.IS_EMAIL.test(value)) {
    return 'APP_VALIDATION_EMAIL_INVALID';
  }  else if (duplicateEmailEnrollment) {
    return duplicateEmailEnrollment.copyID;
  }
}
/**
 * @param  {String} value - name of bank for installment plan
 * @param  {Object} data - additional data
 * @return {String} copyID of error message, if validation fails
 */
export function installmentPlanBankName(value, data) {
  if (isEmpty(value) && data.installmentPlanAgreement) {
    return 'REVIEW_VALIDATION_BANK_NAME';
  }
}

/**
 * @param  {Number} value - number of months for installment plan
 * @param  {Object} data - additional data
 * @return {String} copyID of error message, if validation fails
 */
export function installmentPlanType(value, data) {
  if (isEmpty(value) && data.installmentPlanAgreement) {
    return 'REVIEW_VALIDATION_INSTALLMENT_TYPE';
  }
}

/**
 * @param {Array} arr - Array of Frequent Flyer validations of regEx
 * @param {String} val Value for validate
 * @return {Boolean} If value is correct return true
 */
function isCorrectRule(arr, val) {
  for (let i = 0; i < arr.length; i++) {
    const isCorrectNumber = arr[i].test(val);

    if (isCorrectNumber) {
      return true;
    }
  }
  return false;
}
/**
 * @param {String} value - Number to valid
 * @param {String} frequentFlyerProgram - Frequent Flyer Program
 * @return {Boolean} copyID of error message, if validation fails
 */
const isKoreaNumber = (value, frequentFlyerProgram) => {
  // Validación solicitada en Trello Card 494
  let isCorrectNumber = false;
  if (frequentFlyerNumberRegex[frequentFlyerProgram].test(value)) {
    // Validación de 9 a 10 caracteres Ejemplo: BK44135807
    if (/^(BA|BC|BH|BK|BJ|BL|BM|BU|ba|bc|bh|bk|bj|bl|bm|bu)([0-9]{7,8})$/.test(value)) {
      const justNumbers = value.substr(2);
      const lastCharacter = parseInt(value.substr(-1), 10);
      const numbersChar = justNumbers.slice(0, -1);
      const arryNumbers = numbersChar.split('');
      let sumatoryNumbers = 0;

      for (let i = 0; i < arryNumbers.length; i++) {
        sumatoryNumbers += parseInt(arryNumbers[i], 10);
      }
      const moduleNumber = sumatoryNumbers % 9;
      if (lastCharacter === moduleNumber) {
        isCorrectNumber = true;
      }
    } else if (/^[0-9]{12}$/.test(value)) {
      // Validación de 12 números Ejemplo: 120718134254
      const simpleStringValue = String(value);
      const lastCharacter = parseInt(simpleStringValue.substr(-1), 10);
      const numbersToValidate = simpleStringValue.slice(0, -1);
      const arryNumbers = numbersToValidate.split('');
      let constantKorea = 2;
      let sumatoryNumbers = 0;

      for (let i = 0; i < arryNumbers.length; i++) {
        sumatoryNumbers += parseInt(arryNumbers[i], 10) * constantKorea;
        constantKorea = constantKorea >= 9 ? 2 : constantKorea + 1;
      }
      const moduleNumber = sumatoryNumbers % 10;

      if (lastCharacter === moduleNumber) {
        isCorrectNumber = true;
      }
    }
  }
  return isCorrectNumber;
};

/**
 * @param {String} value - first name entered
 * @return {String} copyID of error message, if validation fails
 */
export function ktn(value) {
  if (!isEmpty(value) && (!isInteger(value) || (value.length < 5 || value.length > 25))) {
    return 'PAX_VALIDATION_KTN_INVALID';
  }
}

/**
 * @param {String} value - cvc entered
 * @param {Object} data - additional data
 * @param {Boolean} data.cardType - type of credit card entered
 * @return {String} error message, if validation fails
 */
export function nameOnCard(value) {
  if (isEmpty(value)) {
    return 'REVIEW_VALIDATION_NAME'; // Please provide the credit card holders' name
  } else if (noSpecialChars(value)) {
    return 'REVIEW_VALIDATION_NAME_NO_SPECIAL_CHARS'; // Please provide a name with no special characters.
  }
}

/**
 * @param {String} value - redress number entered
 * @return {String} copyID of error message, if validation fails
 */
export function redress(value) {
  if (!isEmpty(value) && (!isInteger(value) || (value.length < 7 || value.length > 13))) {
    return 'PAX_VALIDATION_REDRESS_INVALID';
  }
}

/**
 * @param {String} value - redress number entered
 * @return {String} copyID of error message, if validation fails
 */
export function redressNumber(value) {
  if (!isEmpty(value) && (!isInteger(value) || (value.length < 7 || value.length > 13))) {
    return 'PAX_VALIDATION_REDRESS_INVALID';
  }
}

/**
 * @param {String} value - privacy policy
 * @return {String} copyID of error message, if validation fails
 */
export function privacyPolicy(value) {
  if (!value) {
    return 'PAX_VALIDATION_PRIVACY_POLICY';
  }
}

export function privacyPolicyCompany(value) {
  if (!value) {
    return 'PAX_VALIDATION_PRIVACY_POLICY';
  }
}

export function termsAndConditions(value) {
  if (!value) {
    return 'PAX_VALIDATION_TERMS_AND_CONDITIONS';
  }
}

export function birthdayDate(value) {
  if (isEmpty(value) || typeof value !== 'string') {
    return 'PAX_VALIDATION_DOB';
  } else {
    const y = value && parseInt(value.slice(0, 4), 10);
    const m = value && parseInt(value.slice(5, 7), 10);
    const d = value && parseInt(value.slice(8, 10), 10);

    const lastDayOfSelectedMonth = Moment(`${y}-${m}`, 'YYYY-MM').endOf('month').date();
    // check month number
    if (m < 1 || m > 12) {
      // Verify month is in range
      return 'PAX_VALIDATION_MONTH';
    } else if (d > lastDayOfSelectedMonth) {
      // Verify day selected is in month (e.g. only 28/29 days in Feb)
      return {
        copyID: 'PAX_VALIDATION_DATE_MONTH',
        data: Moment(m, 'MM').format('MMMM'),
      };
    }
  }
}

export function birthdayDateAgeMajority(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_DOB';
  } else {
    const stringValue = value.toString();
    const y = stringValue && parseInt(stringValue.slice(0, 4), 10);
    const m = stringValue && parseInt(stringValue.slice(5, 7), 10);
    const d = stringValue && parseInt(stringValue.slice(8, 10), 10);

    const lastDayOfSelectedMonth = Moment(`${y}-${m}`, 'YYYY-MM').endOf('month').date();
    // check month number
    if (m < 1 || m > 12) {
      // Verify month is in range
      return 'PAX_VALIDATION_MONTH';
    } else if (d > lastDayOfSelectedMonth) {
      // Verify day selected is in month (e.g. only 28/29 days in Feb)
      return {
        copyID: 'PAX_VALIDATION_DATE_MONTH',
        data: Moment(m, 'MM').format('MMMM'),
      };
    }
    const selectedDate = new Date(y, m - 1, d);
    const now = new Date();
    const dateMinusEighteenYears = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
    if (selectedDate > dateMinusEighteenYears) {
      return 'PRO_HOME2.0_UNDERAGE_ERROR';
    }
  }
}

export function yearDate(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_DOB';
  } 
}

export function dayB(value) {
  if (isEmpty(value) || value === undefined) {
    return 'PAX_VALIDATION_DOB';
  }
}

export function monthB(value) {
  if (isEmpty(value) || value === undefined) {
    return 'PAX_VALIDATION_DOB';
  }
}

export function yearB(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_DOB';
  }
}
/**
 * @param {String} value - password entered
 */
export function confirmPassword(value, data) {
  if (isEmpty(value)) {
    return 'APP_VALIDATION_PASSWORD_EMPTY';
  } else if (data.password !== value) {
    return 'APP_VALIDATION_PASSWORDS_NEED_MATCH';
  }
}

/**
 * @param {String} value - card number entered
 * @return {String} copyID of error message, if validation fails
 */
export function giftCardNumber(value) {
  if (isEmpty(value)) {
    return 'VALIDATION_GIFT_CARD_NUMBER_EMPTY'; // Please provide a gift card number
  } else {
    // replace all non-numerals
    const num = value.replace(regexMap.NON_NUMERALS, '');
    const isGiftCardNumberUATP = isUATP(num);

    return !isGiftCardNumberUATP ? 'VALIDATION_GIFT_CARD_NUMBER_VALID' : null; // Please provide a valid gift card number
  }
}

/**
 * @param {String} value - lastname entered
 * @return {String} copyID of error message, if validation fails
 */

export function lastFourNumbersPhone(value) {
  if (isEmpty(value) || value?.phoneNumber.length !== 4) {
    return 'PRO_HOME_LOGIN_PHONE_ERROR';
  }
}

export function lastName(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_LAST_NAME'; // Please provide the billing city
  } else if (noSpecialChars(value)) {
    return 'PAX_VALIDATION_LAST_NAME_NO_SPECIAL'; // Please provide a city with no special characters
  }
  if (value) {
    if (value.length < 2) {
      return 'PAX_VALIDATION_LAST_NAME_MIN_CHARS';
    }
  }
}

export function lastNamePvf(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_LAST_NAME';
  } else if (noSpecialChars(value)) {
    return 'PAX_VALIDATION_LAST_NAME_NO_SPECIAL';
  }
  if (value) {
    if (value.length < 2) {
      return 'PAX_VALIDATION_LAST_NAME_MIN_CHARS';
    }
  }
}

export function countryCode(value) {
  if (isEmpty(value)) {
    return 'PAX_SELECT_COUNTRY';
  }
}

export function pnrFrequentFlyerNumber(value, data) {
  if (data.lookupType === lookupTypes.FREQUENT_FLYER) {
    const ffp = data.frequentFlyerProgram;

    if (isEmpty(value) && ffp) {
      return 'PAX_VALIDATION_FFP_EMPTY';
    } else if (!isEmpty(value) && frequentFlyerNumberRegex[ffp] && !frequentFlyerNumberRegex[ffp].test(value)) {
      return 'PAX_VALIDATION_FFP_INVALID';
    } else if (ffp === FFP.AEROMEXICO && !verifyWithLuhnAlgorithm(value.replace(/\s+/g, ''))) {
      return 'CLUB_PREMIER_VALIDATION_FAILURE';
    }
  }
}

export function pnrFFN(value, data) {
  const ffp = data.programFrequentTraveler;
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_FFP_EMPTY';
  } else if (!isEmpty(value) && frequentFlyerNumberRegex[ffp] && !frequentFlyerNumberRegex[ffp].test(value)) {
    return 'PAX_VALIDATION_FFP_INVALID';
  } else if (!verifyWithLuhnAlgorithm(value.replace(/\s+/g, ''))) {
    return 'CLUB_PREMIER_VALIDATION_FAILURE';
  }
}

/**
 * @param {String} value - pnr entered
 * @return {String} copyID of error message, if validation fails
 */
export function ticketNumber(value) {
  if (isEmpty(value)) {
    return 'CHECK_IN_VALIDATION_TICKET_INCORRECT';
  }

  if (value) {
    if (value.length != 6 && value.length != 11) {
      return 'CHECK_IN_VALIDATION_TICKET_INCORRECT';
    } else {
      return '';
    }
  }
}

export function pnrTicketNumber(value, data) {
  if (data.lookupType === lookupTypes.TICKET) {
    if (isEmpty(value)) {
      return 'CHECK_IN_VALIDATION_TICKET_EMPTY';
    } else if (!isAlphanumeric(value) || (!isInteger(value) && isAlphanumeric(value) && !(/^[a-z0-9]{6}$/i.test(value) || /^[A-Za-z]{6}$/.test(value)))) {
      return 'CHECK_IN_VALIDATION_TICKET_INCORRECT';
    } else if (isInteger(value) && !(value.length === 13 || value.length === 6)) {
      return 'CHECK_IN_VALIDATION_NUMBERTICKET_INCORRECT';
    }
  }
}

export function reservationTicketNumber(value, data) {
  if (data.ticketNumber) {
    if (isEmpty(data.ticketNumber)) {
      return 'CHECK_IN_VALIDATION_TICKET_EMTY';
    } else if (!isAlphanumeric(data.ticketNumber) || (!isInteger(data.ticketNumber)) && isAlphanumeric(data.ticketNumber) && !(/^([A-Za-z]{3})([0-9]{3})$/.test(data.ticketNumber) || /^[A-Za-z]{6}$/.test(data.ticketNumber))) {
      return 'CHECK_IN_VALIDATION_TICKET_INCORRECT';
    } else if (isInteger(data.ticketNumber) && data.ticketNumber.length !== 13) {
      return 'CHECK_IN_VALIDATION_TICKET_INCORRECT';
    }
  }
}

/**
 * @param {String} value - email as string or club premier number entered
 */
export function clubPremierNumber(value) {
  const trimmedVal = value && value.replace(/\s/g, '');

  if (isEmpty(trimmedVal)) {
    return 'APP_VALIDATION_CLUB_PREMIER_EMPTY'; // Please provide a valid club premier number
  } else if (!isInteger(trimmedVal) || trimmedVal.length > 11) {
    return 'APP_VALIDATION_CLUB_PREMIER_NUMBER'; // The data has to be numbers and 11 digits
  } else if (!verifyClubPremierNumber(trimmedVal)) {
    return 'APP_VALIDATION_CLUB_PREMIER_FAILURE';
  }
}

/**
 * @param {String} value - email as string or club premier number entered
 */
export function emailClubPremierNumber(value) {
  const trimmedVal = value && value.replace(/\s/g, '');

  if (isNaN(value)) {
    if (!isEmpty(value) && !regexMap.IS_EMAIL.test(value)) {
      return 'APP_VALIDATION_EMAIL_INVALID';
    } else if (isEmpty(trimmedVal)) {
      return 'APP_VALIDATION_CLUB_PREMIER_EMPTY'; // Please provide a valid club premier number
    }
  } else {
    if (isEmpty(trimmedVal)) {
      return 'APP_VALIDATION_CLUB_PREMIER_EMPTY'; // Please provide a valid club premier number
    } else if (trimmedVal?.length > 9) {
      return 'APP_VALIDATION_CLUB_PREMIER_NUMBER'; // The data has to be numbers and 11 digits
    } else if (!verifyClubPremierNumber(trimmedVal)) {
      return 'APP_VALIDATION_CLUB_PREMIER_FAILURE';
    }
  }
}

/**
 * @param {String} value - first name entered
 * @return {String} copyID of error message, if validation fails
 */
export function firstName(value) {
  if (isEmpty(value)) {
    return 'PAX_VALIDATION_FIRST_NAME';
  } else if (!isName(value)) {
    // NOTE: Special chars test must be before min chars (https://trello.com/c/bVDyI7Ql)
    return 'PAX_VALIDATION_FIRST_NAME_NO_SPECIAL';
  } else if (value.length < 2) {
    return 'PAX_VALIDATION_FIRST_NAME_MIN_CHARS';
  }
}

/**
 * @param {String} value - middle name entered
 * @return {String} copyID of error message, if validation fails
 */
export function middleName(value) {
  if (!isEmpty(value) && !isName(value)) {
    // NOTE: Special chars test must be before min chars (https://trello.com/c/bVDyI7Ql)
    return 'PAX_VALIDATION_MIDDLE_NAME_NO_SPECIAL';
  } else if (!isEmpty(value) && value.length < 2) {
    return 'PAX_VALIDATION_MIDDLE_NAME_MIN_CHARS';
  }
}

/**
 * @param {String} value - phone number
 * @param {Object} data - additional data
 * @return {Object/String} object with copyID/data of error message, if validation fails
 */
export function phoneNumberSignUpProfile(value, data = undefined) {
  const phoneNumber = value && value.phoneNumber;

  if (data && isEmpty(deleteSpacesText(phoneNumber))) {
    return 'PAX_VALIDATION_PHONE_EMPTY';
  } else if (!isEmpty(phoneNumber)) {
    return validatePhoneNumber(value);
  }
}

const validatePhoneNumber = (value, errorMessageCopyID = 'APP_VALIDATION_PHONE_INVALID') => {
  let isValid = false;

  try {
    const numberProto = phoneUtil.parse(value.phoneNumber, value.isoCode);
    isValid = phoneUtil.isValidNumber(numberProto);
  } catch (e) {
    console.log(e); // eslint-disable-line no-console
  }

  if (!isValid) {
    return {
      copyID: errorMessageCopyID,
      data: value.isoCode,
    };
  }
};
/**
 * @param {String} value - password entered
 */
export function password(value) {
  if (isEmpty(value)) {
    return 'APP_VALIDATION_PASSWORD_EMPTY';
  } else if (value.length > 25) {
    return 'APP_VALIDATION_PASSWORD_MAXLENGTH';
  } else if (!isAlphanumericBoth(value)) {
    return 'APP_VALIDATION_PASSWORD_BOTH_CHARACTERS';
  }
}

export function telephonePIN(value) {
  if (isEmpty(value) || !isNumeric(value) || value.length < 4) {
    return 'PRO_HOME2.0_NIP_ERROR';
  } else if (!consecutiveDigits(value)) {
    return 'PRO_HOME2.0_NIP_ERROR_EQUAL';
  } else if (('01234567890'.includes(value) || '9876543210'.includes(value))) {
    return 'PRO_HOME2.0_NIP_ERROR_SEQUENCE';
  }
}

export function passwordCP(value) {
  if (isEmpty (value)) {
    return 'APP_VALIDATION_PASSWORD_EMPTY';
  } else if (value.length > 25 || value.length < 8) {
    return 'APP_VALIDATION_PASSWORD_MAXLENGTH';
  } else if (!isPasswordCP (value)) {
    return  'APP_VALIDATION_PASSWORD_BOTH_CHARACTERS';
  } else if (isPasswordCE (value) || (/([´!`¨\]/·\[/≤+÷(/\/)(\-/)(\_/)(())(@#$%^~&*(,.;?¿¡"'=):{}(|<>|ñÑ])/g.test(value))) {
    return 'APP_VALIDATION_PASSWORD_SPECIAL_CHARACTERS';
  }
}

/**
 * @param {String} value - voucher number entered
 * @return {String} copyID of error message, if validation fails
 */
export function voucherNumber(value) {
  if (isEmpty(value)) {
    return 'EMD_VOUCHER_NOT_VALID'; // Please provide a voucher number
  } else if (regexMap.NON_NUMERALS.test(value)) {
    return 'EMD_VOUCHER_NOT_VALID'; // Please provide a valid voucher number
  } else {
    const isInvalid = !isEMDVoucher(value);
    return isInvalid ? 'EMD_VOUCHER_NOT_VALID' : null; // Please provide a valid voucher number
  }
}

export function gender(value) {
  if (!value) {
    return 'PAX_VALIDATION_GENDER';
  }
}

export function createValidator(rules) {
  return (values = {}) => {
    const errors = {};

    Object.keys(rules).forEach((key) => {
      const rule = join([].concat(rules[key]));

      const error = rule(values[key], values);

      if (error) {
        errors[key] = error;
      }
    });

    return errors;
  };
}

function _AeroflotValidation(value) {
  if (isEmpty(value))
    return 'PAX_VALIDATION_FFP_EMPTY';

  if (!isInteger(value))
    return 'PAX_VALIDATION_FFP_INVALID';

  const valueNum = parseInt(value, 10);

  if (valueNum <= 20198716) {
    if (!_isValidOld(value))
      return 'PAX_VALIDATION_FFP_INVALID';
  } else {
    if (!_validaNewAeroflot(value))
      return 'PAX_VALIDATION_FFP_INVALID';
  }
}

function _isValidOld(value) {
  let paddedValue = '';
  let total = 0;

  if (value.length < 10) {
    paddedValue = value.padStart(10, '0');
  }

  for (let i = 0; i < paddedValue.length - 1; i++) {
    const valor = parseInt(paddedValue.charAt(i), 10);
    total = total + (valor * (i + 1));
  }
  const strDigits = total.toString();
  const lastDigit = strDigits.charAt(strDigits.length - 1);
  const valueLastDigit = value.charAt(value.length - 1);

  return (lastDigit === valueLastDigit);
}

function _validaNewAeroflot(value) {
  if (value.length < 8 || value.length > 10)
    return false;

  const valueWODigit = value.substr(0, value.length - 1);
  const valueInt = parseInt(valueWODigit, 10);
  const truncValue = Math.trunc(valueInt / 7);
  const newValue = truncValue * 7;
  const checkDigit = parseInt(valueWODigit, 10) - newValue;
  const originalCheckDigit = value.substr(value.length - 1, 1);
  const originalIntCheckDigit = parseInt(originalCheckDigit, 10);

  return checkDigit === originalIntCheckDigit;
}

export function companyPolicy(value) {
  if (!value) {
    return 'PAX_VALIDATION_COMPANY_POLICY';
  }
}

export function recoverTelephonePIN(value) {
  if (isEmpty(value) || !isNumeric(value) || value.length < 4) {
    return 'PRO_HOME2.0_NIP_ERROR';
  } else if (!consecutiveDigits(value)) {
    return 'PRO_HOME2.0_NIP_ERROR';
  } else if (('01234567890'.includes(value) || '9876543210'.includes(value))) {
    return 'PRO_HOME2.0_NIP_ERROR';
  }
}